import React, { useState, useEffect } from "react";
import moment from "moment";
import {
    Form,
    Input,
    Button,
    Modal,
    message,
    Upload,
    Select,
    DatePicker,
    Typography,
} from "antd";
import { createUseStyles } from "react-jss";
import ReCAPTCHA from "react-google-recaptcha";
import * as lodash from "lodash";
import { useIntl } from "react-intl";
import { connect, useSelector } from "react-redux";
import { UploadOutlined, EyeOutlined, EyeInvisibleOutlined } from "@ant-design/icons";
import { UploadFile } from "antd/lib/upload/interface";
import type { RcFile } from "antd/es/upload";

import { GOOGLE_RECAPTCHA_SITE_KEY } from "../../../globals";
import {
    registerAppUser,
    putImageS3,
    getAppUserOtp,
    riderOnboardingPostFunction,
} from "../../../api/appUserSignup";
const { Option } = Select;
const { Text, Link } = Typography;

const styles = createUseStyles({
    header: {
        padding: "8px 24px",
        boxShadow: "0px 0px 10px rgba(0,0,0,0.4)",
        fontSize: 16,
        fontWeight: 600,
        color: "#424242",
        alignItems: "center",
        position: "fixed",
        top: 0,
        right: 0,
        left: 0,
        background: "#ffffff",
        zIndex: +1,
    },
    bookingFormFields: {
        padding: 36,
        boxSizing: "border-box",
        "& label::after": {
            content: '"" !important',
        },
        "& label.ant-form-item-required::before": {
            display: "inline-block",
            marginRight: "4px",
            color: "#ff4d4f",
            fontSize: "14px",
            lineHeight: 1,
            content: '"*" !important',
        },
        "& label.ant-radio-wrapper": {
            minWidth: "120px",
        },
        "& .ant-form-item-control-input": {
            maxWidth: "400px",
        },
        "& .ant-input-number ": {
            width: "100%",
        },
    },
    phoneNumberField: {
        "& .ant-input-group-rtl": {
            direction: "ltr",
            borderLeft: "1px solid #d9d9d9",
        },
    },
    formSectionHeader: {
        fontSize: 16,
        fontWeight: 600,
        color: "#424242",
        marginBottom: 20,
    },
    uploadField: {
        display: "flex",
        "& p": {
            color: "#A6B6C3",
            fontSize: "0.8rem",
        },
        "& .ant-upload-select-picture-card": {
            width: "100%",
            height: "auto",
            padding: "2rem",
        },
        "& .ant-upload-list-picture-card": {
            width: "100%",
        },
        "& .ant-upload": {
            flexDirection: "column",
        },
    },
    uploadIcon: {
        fontSize: "2rem",
        color: "#A6B6C3",
        padding: "1rem 0",
    },
    verifyButton: {
        color: "#1890ff",
        cursor: 'pointer',
        fontWeight: 500,
    },
});

interface IdentificationDocument {
    type: string;
    number: string;
    image: string;
}

interface PocContentType {
    type: string;
    contentTypeList: String[];
}

const getBase64 = (file: RcFile): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = (error) => reject(error);
    });

const QwiklogisticsRiderOnboardingForm = (props: any) => {
    const intl = useIntl();
    const classes = styles();
    const [googleCaptha, setGoogleCaptcha] = useState(null);
    const [otpGoogleCaptcha, setOTPGoogleCaptcha] = useState(null);
    const [aadharCardGoogleCaptcha, setAadharCardGoogleCaptcha] = useState(null);
    const [drivingLicenceGoogleCaptcha, setDrivingLicenceGoogleCaptcha] = useState(null);
    const [captchaError, setCaptchaError] = useState("");
    const [otpCaptchaError, setOTPCaptchaError] = useState("");
    const [aadharCardCaptchaError, setAadharCardCaptchaError] = useState("");
    const [drivingLicenceCaptchaError, setDrivingLicenceCaptchaError] = useState("");
    const [otpHash, setOtpHash] = useState("");
    const [visibleOtp, setVisibleOtp] = useState(false);
    const [digilockerValidationFailed, setIsDigilockerValidationFailed] = useState(false);
    const toggleVisibility = () => {
        setVisibleOtp((prev) => !prev);
    };
    const [processingSubmit, setProcessingSubmit] = useState(false);
    const [processingVerify, setProcessingVerify] = useState(false);
    const [formReset, setFormReset] = useState(0);
    const language = useSelector((state: any) => state.translation.language);
    const [form] = Form.useForm();
    const [modal, contextHolder] = Modal.useModal();
    const [isMobileNumberLengthValid, setMobileNumberLengthValid] = React.useState(false);
    const [isAadharNumberLengthValid, setAadharNumberLengthValid] = React.useState(false);
    const [isDrivingLicenceNumberLengthValid, setDrivingLicenceNumberLengthValid] = React.useState(false);
    const [isWorkerTypeDriver, setIsWorkerTypeDriver] = React.useState(false);
    const [otpFetched, setOtpFetched] = React.useState(false);
    const [aadharCardVerified, setAadharCardVerified] = React.useState(false);
    const [drivingLicenseVerified, setDrivingLicenseVerified] = React.useState(false);
    const [aadharJwtToken, setAadharJwtToken] = React.useState("");
    const [drivingLicenseJwtToken, setDrivingLicenseJwtToken] = React.useState("");

    const [fileList, setFileList] = useState({
        drivingLicence: [] as UploadFile[],
        aadhaarCard: [] as UploadFile[],
    });
    const [previewOpen, setPreviewOpen] = useState({
        drivingLicence: false,
        aadhaarCard: false,
    });
    const [previewImage, setPreviewImage] = useState({
        drivingLicence: "",
        aadhaarCard: "",
    });
    const [previewTitle, setPreviewTitle] = useState({
        drivingLicence: "",
        aadhaarCard: "",
    });
    const workerTypeEnum = {
        DRIVER: "Driver",
        SELF: "Self",
    }
    const workerTypeLabelEnum = {
        DRIVER: 'Driver',
        SELF: 'Self (Helper)'
    }
    const selfOnboardingWorkerTypes = [
        {
            key: workerTypeEnum.DRIVER,
            value: workerTypeEnum.DRIVER,
            label: workerTypeLabelEnum.DRIVER
        },
        {
            key: workerTypeEnum.SELF,
            value: workerTypeEnum.SELF,
            label: workerTypeLabelEnum.SELF
        }
    ];
    const identificationDocuments = [{
        key: 'aadhaarCard',
        label: intl.formatMessage({ id: "aadhaarID" }),
    }, {
        key: 'drivingLicence',
        label: intl.formatMessage({ id: "drivingLicence" }),
    }];

    const generateNationalIDTypeOptions = () => {
        const options = identificationDocuments.map((e, index) =>
            <Option
                key={index}
                value={e.key}>{e.label}
            </Option>
        );
        return options;
    };

    useEffect(() => {
        setGoogleCaptcha(null);
        setOTPGoogleCaptcha(null);
        setAadharCardGoogleCaptcha(null);
        setDrivingLicenceGoogleCaptcha(null);
    }, [language]);

    const validateFileType = (file) => {
        const isLt5M = file.size / 1024 / 1024 < 5;
        const isAllowedType = isLt5M;
        if (!isAllowedType) {
            return false;
        }
        return true;
    };

    const uploadProps = (type) => {
        return {
            beforeUpload: (file) => {
                if (!validateFileType(file)) {
                    message.error("Image must smaller than 5MB!");
                    return Upload.LIST_IGNORE;
                }
                return true;
            },
            onRemove: (file: UploadFile) => {
                if (fileList[type].some((item) => item.uid === file.uid)) {
                    setFileList({
                        ...fileList,
                        [type]: fileList[type].filter((item) => item.uid !== file.uid),
                    });
                    return true;
                }
                return false;
            },
            onPreview: async (file: UploadFile) => {
                if (!file.url && !file.preview) {
                    file.preview = await getBase64(file.originFileObj as RcFile);
                }
                setPreviewImage({
                    ...previewImage,
                    [type]: file.url || (file.preview as string),
                });
                setPreviewOpen({
                    ...previewOpen,
                    [type]: true,
                });
                setPreviewTitle({
                    ...previewTitle,
                    [type]:
                        file.name || file.url!.substring(file.url!.lastIndexOf("/") + 1),
                });
            },
            onChange: ({ file, fileList: newFileList }) => {
                setFileList({
                    ...fileList,
                    [type]: newFileList,
                });
            },
        };
    };

    const uploadImages = async (uploadUrl, type) => {
        try {
            const fileUploads = fileList[type].map(async (image, index) => {
                const fileItem = fileList[type]?.[index];
                const signedUrl = uploadUrl[index];
                const file = new Blob([fileItem.originFileObj as BlobPart], {
                    type: fileItem.type,
                });

                await putImageS3(signedUrl, file, fileItem.type);
            });
            await Promise.all(fileUploads);
            return fileUploads;
        } catch (err) {
            console.log(err);
            modal.error({
                title: "Could Not Process Request",
                content: "Please try again later",
                onOk() {
                    setProcessingSubmit(false);
                },
            });
        }
    };

    const getImageContentTypeList = (type) => {
        const contentType = [] as String[];
        fileList[type].map(async (image) => {
            const fileItem = image;
            contentType.push(fileItem.type?.split("/")[1]);
        });
        return contentType;
    };

    const submitForm = async (values: any) => {
        setProcessingSubmit(true);
        let isImageUploaded = {
            drivingLicence: false,
            aadhaarCard: false,
        };

        const captcha = googleCaptha;
        if (!captcha) {
            setCaptchaError(intl.formatMessage({ id: "captchaError" }));
            setProcessingSubmit(false);
            return;
        }
        if (values.workerType === workerTypeEnum.DRIVER && !drivingLicenseVerified) {
            message.error('Please verify Driving License first!');
            setProcessingSubmit(false);
            return;
        }
        if (!drivingLicenseJwtToken && !aadharJwtToken) {
            message.error('Please verify Aadhar Card and Driving License first!');
            setProcessingSubmit(false);
            return;
        }
        var name = values.name;
        const firstName = values.firstName;
        const lastName = values.lastName;
        if (values.lastName && values.firstName) {
            name = values.firstName + (values.middleName ? ' ' + values.middleName : '') + ' ' + values.lastName;
        }
        const requestData = {
            ...values,
            type: "CREATEWORKER",
            objectType: "WORKER",
            jwtToken: drivingLicenseJwtToken || aadharJwtToken,
            name,
            firstName,
            lastName,
            captcha,
            nationalIdType: values.nationalIdType,
            dob: moment(values.dob).format('DDMMYYYY'),
        } as { [key: string]: any };
        if (!otpFetched) {
            message.error('Please verify phone number!');
            setProcessingSubmit(false);
            return;
        }
        if (!otpHash) {
            message.error('Please get OTP!');
            setProcessingSubmit(false);
            return;
        }
        requestData.uidNumber = values.aadhaarCard;
        requestData.dlNumber = values.drivingLicence;
        requestData.mobileNumber = values.phoneNumber;
        requestData.otp = values.otp;
        requestData.hash = otpHash;

        if (form.getFieldValue('nationalIdType')) {
            requestData.nationalIdNumber = values[form.getFieldValue('nationalIdType')] || '';
        }
        if (form.getFieldValue('hubCode')) {
            requestData.preferredLocality = form.getFieldValue('hubCode');
        }

        const identificationDocuments: IdentificationDocument[] = [];
        const pocContentTypeList: PocContentType[] = [];
        if (values.aadhaarCard && getImageContentTypeList("aadhaarCard")) {
            const aadhaarCardPocContentTypeList = getImageContentTypeList("aadhaarCard");
            pocContentTypeList.push({ type: 'aadhaarCard', contentTypeList: aadhaarCardPocContentTypeList });
            identificationDocuments.push({ type: 'aadhaarCard', number: values.aadhaarCard, image: '' });
            delete requestData.aadhaarCard;
        }
        if (values.drivingLicence && getImageContentTypeList("drivingLicence")) {
            const drivingLicencePocContentTypeList = getImageContentTypeList("drivingLicence");
            pocContentTypeList.push({ type: 'drivingLicence', contentTypeList: drivingLicencePocContentTypeList });
            identificationDocuments.push({ type: 'drivingLicence', number: values.drivingLicence, image: '' });
            delete requestData.drivingLicence;
        }
        requestData.identificationDocuments = identificationDocuments;
        requestData.pocContentTypeList = pocContentTypeList;
        console.log(requestData);

        const response = await registerAppUser(requestData);
        if (response.isSuccess) {
            const imageUploadUrl = {
                drivingLicence: lodash.get(
                    response,
                    "data.data.drivingLicenceNumberSignedUrlList",
                    []
                ),
                aadhaarCard: lodash.get(
                    response,
                    "data.data.aadhaarCardNumberSignedUrlList",
                    []
                ),
            };
            await uploadImages(
                imageUploadUrl["drivingLicence"],
                "drivingLicence"
            ).then(() => {
                isImageUploaded = {
                    ...isImageUploaded,
                    drivingLicence: true,
                };
            });
            await uploadImages(
                imageUploadUrl["aadhaarCard"],
                "aadhaarCard"
            ).then(() => {
                isImageUploaded = {
                    ...isImageUploaded,
                    aadhaarCard: true,
                };
            });

            if (isImageUploaded.drivingLicence && isImageUploaded.aadhaarCard) {
                modal.success({
                    title: "Registration confirmed!",
                    content: (
                        <p>{intl.formatMessage({ id: "registrationConfirmed" })} </p>
                    ),
                    onOk() {
                        form.resetFields();
                        setFileList({
                            drivingLicence: [] as UploadFile[],
                            aadhaarCard: [] as UploadFile[],
                        });
                        setPreviewTitle({
                            drivingLicence: "",
                            aadhaarCard: "",
                        });
                        setPreviewImage({
                            drivingLicence: "",
                            aadhaarCard: "",
                        });
                        setFormReset(Number(!formReset));
                        setProcessingSubmit(false);
                    },
                });
            } else {
                modal.error({
                    title: "Could Not Process Request",
                    content:
                        "There was a problem uploading the images, please try again in a moment.",
                    onOk() {
                        setProcessingSubmit(false);
                    },
                });
            }

        } else {
            modal.error({
                title: "Could Not Process Request",
                content: response.errorMessage,
                onOk() {
                    setProcessingSubmit(false);
                },
            });
        }
    };

    const captchaChange = (captchaVal: any) => {
        setGoogleCaptcha(captchaVal);
        if (captchaVal && captchaError) {
            setCaptchaError("");
        }
    };
    const otpCaptchaChange = (captchaVal: any) => {
        setOTPGoogleCaptcha(captchaVal);
        if (captchaVal && otpCaptchaError) {
            setOTPCaptchaError("");
        }
    };
    const aadharCardCaptchaChange = (captchaVal: any) => {
        setAadharCardGoogleCaptcha(captchaVal);
        if (captchaVal && aadharCardCaptchaError) {
            setAadharCardCaptchaError("");
        }
    };
    const drivingLicenceCaptchaChange = (captchaVal: any) => {
        setDrivingLicenceGoogleCaptcha(captchaVal);
        if (captchaVal && drivingLicenceCaptchaError) {
            setDrivingLicenceCaptchaError("");
        }
    }

    const genderList = [
        {
            key: intl.formatMessage({ id: "genderMale" }),
            value: 'M'
        },
        {
            key: intl.formatMessage({ id: "genderFemale" }),
            value: 'F'
        },
        {
            key: intl.formatMessage({ id: "genderTransgender" }),
            value: 'T'
        }
    ];

    const generateGenderOptions = (featureItems: any) => {
        return featureItems?.map((d: any) => {
            return {
                value: d?.value,
                label: d?.key,
            };
        });
    };

    const generateWorkerTypeOption = (selfOnboardingWorkerTypes) => {
        return selfOnboardingWorkerTypes?.map((opt: any) => {
            return {
                value: opt.value,
                key: opt.value,
                label: opt.label,
            };
        })
    };

    const handleNameChange = (e, formField) => {
        // remove trailing space or special characters
        if (e.target.value) {
            let value = e.target.value.replace(/[^a-zA-Z\s.'-]/g, '');
            value = (value || '').replace(/\s+$/, ' ')
            form.setFieldValue(formField, value);
        }
        else {
            form.setFieldValue(formField, '');
        }
    }

    const validateAge = (rule, value) => {
        if (value && moment().diff(value, 'years') < 21) {
            return Promise.reject('You must be at least 21 years old!');
        }
        return Promise.resolve();
    }

    const handleCancel = (type) => {
        setPreviewOpen({
            ...previewOpen,
            [type]: false,
        });
    };

    const getOtp = async () => {
        setProcessingVerify(true);
        if (!form.getFieldValue('phoneNumber')) {
            message.error('Please enter phone number!');
            setProcessingVerify(false);
            return;
        }
        const captcha = otpGoogleCaptcha;
        if (!captcha) {
            setOTPCaptchaError(intl.formatMessage({ id: "otpCaptchaError" }));
            setProcessingVerify(false);
            return;
        }
        const response = await getAppUserOtp({
            phone: form.getFieldValue('phoneNumber'),
            captcha: captcha,
        });
        if (response.isSuccess && response.data) {
            setOtpFetched(true);
            setOtpHash(response.data);
        } else {
            message.error(response.errorMessage);
        }
        setProcessingVerify(false);
    }
    const verifyAadharCardAndCaptcha = async () => {
        setProcessingVerify(true);
        if (!form.getFieldValue('aadhaarCard')) {
            message.error('Please enter Aadhar Card Number!');
            setProcessingVerify(false);
            return;
        }
        const captcha = aadharCardGoogleCaptcha;
        if (!captcha) {
            setAadharCardCaptchaError(intl.formatMessage({ id: "captchaError" }));
            setProcessingVerify(false);
            return;
        }
        const body = {
            mobileNumber: form.getFieldValue('phoneNumber'),
            captcha,
            uidNumber: form.getFieldValue('aadhaarCard'),
            name: form.getFieldValue('firstName') + ' '
                + (form.getFieldValue('middleName') ? form.getFieldValue('middleName') + ' ' : '')
                + form.getFieldValue('lastName'),
            dob: moment(form.getFieldValue('dob')).format('DDMMYYYY'),
            gender: form.getFieldValue('gender')
        };

        const response = await riderOnboardingPostFunction('AADHAR_CARD_DETAILS_VERIFY_URL', body);

        if (response.isSuccess && response?.data?.token) {
            setAadharJwtToken(response?.data?.token);
            setAadharCardVerified(true);
        } else {
            if(response.errorMessage === 'Code or Code Verifier not found'){
                setIsDigilockerValidationFailed(true);
                message.error('Digilocker Account not found');
            } else {
                message.error(response.errorMessage || 'Unable to authenticate Aadhar Details!');
            }
        }
        setProcessingVerify(false);
    }
    const verifyDrivingLicenseAndCaptcha = async () => {
        setProcessingVerify(true);
        if (!form.getFieldValue('drivingLicence')) {
            message.error('Please enter Driving License Number!');
            setProcessingVerify(false);
            return;
        }
        const captcha = drivingLicenceGoogleCaptcha;
        if (!captcha) {
            setDrivingLicenceCaptchaError(intl.formatMessage({ id: "captchaError" }));
            setProcessingVerify(false);
            return;
        }
        if (!aadharJwtToken) {
            message.error('Please verify Aadhar Card first!');
            setProcessingVerify(false);
            return;
        }
        
        const body = {
            mobileNumber: form.getFieldValue('phoneNumber'),
            captcha,
            uidNumber: form.getFieldValue('aadhaarCard'),
            name: form.getFieldValue('firstName') + ' '
                + (form.getFieldValue('middleName') ? form.getFieldValue('middleName') + ' ' : '')
                + form.getFieldValue('lastName'),
            dob: moment(form.getFieldValue('dob')).format('DDMMYYYY'),
            gender: form.getFieldValue('gender'),
            dlNumber: form.getFieldValue('drivingLicence'),
            aadharJwtToken
        };

        const response = await riderOnboardingPostFunction('DRIVING_LICENSE_DETAILS_VERIFY_URL', body);
        
        if (response.isSuccess && response?.data?.token) {
            setDrivingLicenseJwtToken(response?.data?.token);
            setDrivingLicenseVerified(true);
        } else {
            message.error(response.errorMessage || 'Unable to authenticate Driving License Details!');
        }
        setProcessingVerify(false);
    }

    return (<>
        <Form
            form={form}
            onFinish={submitForm}
            labelCol={{ span: 5 }}
            autoComplete="off"
            wrapperCol={{ span: 12 }}
            labelAlign="left"
        >
            {contextHolder}
            <div className={classes.bookingFormFields}>
                <div className={classes.formSectionHeader}>
                    {intl.formatMessage({ id: "personalDetails" })} :
                </div>
                <Form.Item
                    label={`${intl.formatMessage({ id: "workerType" })} ::`}
                    name="workerType"
                    rules={[
                        {
                            required: true,
                            message: `${intl.formatMessage({ id: "workerType" })} is required!`,
                        },
                    ]}
                >
                    <Select
                        placeholder={`${intl.formatMessage({ id: "workerType" })}`}
                        defaultActiveFirstOption={false}
                        disabled={processingVerify || processingSubmit || digilockerValidationFailed}
                        showArrow={false}
                        onChange={(e) => {
                            form.setFieldsValue({ 'workerType': e });
                            setIsWorkerTypeDriver(e === workerTypeEnum.DRIVER);
                        }}
                    >
                        {generateWorkerTypeOption(selfOnboardingWorkerTypes).map((opt) => (
                            <Option key={opt.key} value={opt.value}>
                                {opt.label}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>
                <Form.Item
                    label={`${intl.formatMessage({ id: "firstName" })} ::`}
                    name="firstName"
                    rules={[
                        {
                            required: true,
                            message: `${intl.formatMessage({ id: "firstName" })} is required!`,
                        }
                    ]}
                >
                    <Input
                        placeholder={intl.formatMessage({ id: "firstName" })}
                        value={form.getFieldValue('firstName')}
                        onChange={(e) => handleNameChange(e, 'firstName')}
                        disabled={aadharCardVerified || processingVerify || processingSubmit || digilockerValidationFailed}
                    />
                </Form.Item>
                <Form.Item
                    label={`${intl.formatMessage({ id: "middleName" })} ::`}
                    name="middleName"
                    rules={[
                        {
                            required: false,
                        },
                    ]}
                >
                    <Input
                        placeholder={intl.formatMessage({ id: "middleName" })}
                        value={form.getFieldValue('middleName')}
                        onChange={(e) => handleNameChange(e, 'middleName')}
                        disabled={aadharCardVerified || processingVerify || processingSubmit || digilockerValidationFailed}
                    />
                </Form.Item>
                <Form.Item
                    label={`${intl.formatMessage({ id: "lastName" })} ::`}
                    name="lastName"
                    rules={[
                        {
                            required: true,
                            message: `${intl.formatMessage({ id: "lastName" })} is required!`,
                        },
                    ]}
                >
                    <Input
                        placeholder={intl.formatMessage({ id: "lastName" })}
                        value={form.getFieldValue('lastName')}
                        onChange={(e) => handleNameChange(e, 'lastName')}
                        disabled={aadharCardVerified || processingVerify || processingSubmit || digilockerValidationFailed}
                    />
                </Form.Item>
                <><Form.Item
                    label={`${intl.formatMessage({ id: "phoneNumber" })} ::`}
                    name="phoneNumber"
                    rules={[
                        {
                            required: true,
                            message: `${intl.formatMessage({ id: "phoneNumber" })} is required!`,
                        },
                    ]}
                >
                    <Input
                        onChange={(e) => {
                            const numberOnly = e.target.value.replace(/\D/g, "");
                            form.setFieldValue('phoneNumber', numberOnly);
                            setMobileNumberLengthValid(numberOnly.length === 10);
                        }}
                        disabled={otpFetched || processingVerify || processingSubmit || digilockerValidationFailed}
                        maxLength={10}
                        placeholder={intl.formatMessage({ id: "phoneNumber" })}
                    />
                </Form.Item>
                </>
                {
                    isMobileNumberLengthValid && !otpFetched ? (
                        <>
                            <Form.Item
                                label=":">
                                <div>
                                    <ReCAPTCHA
                                        sitekey={GOOGLE_RECAPTCHA_SITE_KEY}
                                        onChange={otpCaptchaChange}
                                        hl={language}
                                        key={`${language}${formReset}`}
                                    />
                                    <span style={{ color: "red" }}>{otpCaptchaError}</span>
                                </div>
                            </Form.Item>
                        </>) : null
                }
                {
                    !otpFetched ? (
                        <>
                            <Form.Item label=":">
                                <div>
                                    <Button
                                        className={classes.verifyButton}
                                        loading={processingVerify}
                                        disabled={processingVerify || processingSubmit || !isMobileNumberLengthValid || digilockerValidationFailed}
                                        onClick={getOtp}>
                                        {intl.formatMessage({ id: "verifyPhoneNumber" })}
                                    </Button>
                                </div>
                            </Form.Item>
                        </>) : null
                }
                {otpFetched ?
                    <><Form.Item
                        label={`${intl.formatMessage({ id: "otp" })} ::`}
                        name="otp"
                        rules={[
                            {
                                required: true,
                                message: `${intl.formatMessage({ id: "otp" })} is required!`,
                            },
                        ]}
                    >
                        <Input
                            disabled={processingVerify || processingSubmit || digilockerValidationFailed}
                            type={visibleOtp ? "text" : "password"}
                            value={form.getFieldValue('otp')}
                            placeholder={intl.formatMessage({ id: "otpEnter" })}
                            onChange={(e) => {
                                const numberOnly = e.target.value.replace(/\D/g, "");
                                form.setFieldValue('otp', numberOnly);
                            }}
                            suffix={
                                visibleOtp ? (
                                    <EyeInvisibleOutlined onClick={toggleVisibility} />
                                ) : (
                                    <EyeOutlined onClick={toggleVisibility} />
                                )
                            }
                        />
                    </Form.Item>
                    </>
                    : null
                }
                <Form.Item
                    label={`${intl.formatMessage({ id: "gender" })} ::`}
                    name="gender"
                    rules={[
                        {
                            required: true,
                            message: `${intl.formatMessage({ id: "gender" })} is required!`,
                        },
                    ]}
                >
                    <Select
                        showSearch
                        allowClear
                        disabled={aadharCardVerified || processingVerify || processingSubmit || digilockerValidationFailed}
                        placeholder={intl.formatMessage({ id: "selectGender" })}
                        defaultActiveFirstOption={false}
                        showArrow={false}
                        notFoundContent={null}
                        options={generateGenderOptions(genderList)}
                    />
                </Form.Item>
                <Form.Item
                    label={`${intl.formatMessage({ id: "dob" })} ::`}
                    name="dob"
                    rules={[
                        {
                            required: true,
                            message: `${intl.formatMessage({ id: "dob" })} is required!`,
                        },
                        {
                            validator: validateAge
                        }
                    ]}
                >
                    <DatePicker
                        style={{ width: '100%' }}
                        disabled={aadharCardVerified || processingVerify || processingSubmit || digilockerValidationFailed}
                        defaultPickerValue={moment().subtract(21, "years")}
                        disabledDate={(current) => current && current > moment().subtract(21, "years")}
                        placeholder={intl.formatMessage({ id: "dob" })}
                        format={'DD-MM-YYYY'}
                    />
                </Form.Item>
                <Form.Item
                    label={`${intl.formatMessage({ id: "nationalIdType" })} ::`}
                    name="nationalIdType"
                    rules={[
                        {
                            required: true,
                            message: `${intl.formatMessage({ id: "nationalIdType" })} is required!`,
                        },
                    ]}
                >
                    <Select
                        showSearch
                        allowClear
                        optionFilterProp="children"
                        disabled={processingVerify || processingSubmit || digilockerValidationFailed}
                        filterOption={(input: string, option: any) =>
                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        placeholder={`${intl.formatMessage({ id: 'nationalIdType' })}`}
                    >
                        {generateNationalIDTypeOptions()}
                    </Select>
                </Form.Item>
                <Form.Item
                    label={`${intl.formatMessage({ id: "hubCode" })} ::`}
                    name="hubCode"
                    rules={[
                        {
                            required: true,
                            message: `${intl.formatMessage({ id: "hubCode" })} is required!`,
                        },
                    ]}
                >
                    <Input
                        placeholder={intl.formatMessage({ id: "hubCode" })}
                        disabled={processingVerify || processingSubmit || digilockerValidationFailed}
                    />
                </Form.Item>
                <Form.Item
                    label={`${intl.formatMessage({ id: 'aadhaarCardNumber' })} ::`}
                    name={"aadhaarCard"}
                    rules={[
                        {
                            required: true,
                            message: `${intl.formatMessage({ id: "aadharCardNumber" })} is required!`,
                        },
                    ]}
                >
                    <Input
                        placeholder={intl.formatMessage({ id: 'aadhaarCardNumber' })}
                        maxLength={12}
                        onChange={(e) => {
                            const numberOnly = e.target.value.replace(/\D/g, "");
                            form.setFieldValue('aadhaarCard', numberOnly);
                            setAadharNumberLengthValid(numberOnly.length === 12);
                        }}
                        disabled={(aadharCardVerified && otpFetched) || processingVerify || processingSubmit || digilockerValidationFailed}
                    />
                </Form.Item>
                { digilockerValidationFailed ? 
                    <Text>
                        You need a DigiLocker account to complete onboarding, It helps us verify your identity. Create your account <Link href="https://www.digilocker.gov.in/web/signup" target="_blank">Here</Link>
                    </Text>
                : null}
                {isAadharNumberLengthValid && !aadharCardVerified ? (<Form.Item label=":">
                    <div>
                        <ReCAPTCHA
                            sitekey={GOOGLE_RECAPTCHA_SITE_KEY}
                            onChange={aadharCardCaptchaChange}
                            hl={language}
                            key={`${language}${formReset}`}
                        />
                        <span style={{ color: "red" }}>{aadharCardCaptchaError}</span>
                    </div>
                </Form.Item>) : null}
                {!aadharCardVerified ? (<Form.Item label=":">
                    <div>
                        <Button
                            className={classes.verifyButton}
                            loading={processingVerify}
                            disabled={!otpFetched || processingVerify || processingSubmit || !isAadharNumberLengthValid || digilockerValidationFailed}
                            onClick={verifyAadharCardAndCaptcha}>
                            {intl.formatMessage({ id: "verifyAadhaarCard" })}
                        </Button>
                    </div>
                </Form.Item>) : null}
                <Form.Item
                    label={`${intl.formatMessage({ id: 'drivingLicenceNumber' })} ::`}
                    name={'drivingLicence'}
                    dependencies={["workerType"]}
                    rules={[
                        ({ getFieldValue }) => ({
                            required: getFieldValue('workerType') === workerTypeEnum.DRIVER,
                            message: `${intl.formatMessage({ id: "drivingLicenceNumber" })} is required!`,
                        }),
                    ]}
                >
                    <Input
                        placeholder={intl.formatMessage({ id: 'drivingLicenceNumber' })}
                        onChange={(e) => {
                            const value = e.target.value;
                            const cleanedValue = value.replace(/[^a-zA-Z0-9/]/g, "").toUpperCase();
                            form.setFieldValue('drivingLicence', cleanedValue);
                            setDrivingLicenceNumberLengthValid(cleanedValue.length === 15);
                        }}
                        maxLength={15}
                        disabled={drivingLicenseVerified || processingVerify || processingSubmit || digilockerValidationFailed}
                    />
                </Form.Item>
                {isDrivingLicenceNumberLengthValid && !drivingLicenseVerified ? (<Form.Item label=":">
                    <div>
                        <ReCAPTCHA
                            sitekey={GOOGLE_RECAPTCHA_SITE_KEY}
                            onChange={drivingLicenceCaptchaChange}
                            hl={language}
                            key={`${language}${formReset}`}
                        />
                        <span style={{ color: "red" }}>{drivingLicenceCaptchaError}</span>
                    </div>
                </Form.Item>) : null}
                {!drivingLicenseVerified ? (<Form.Item label=":">
                    <div>
                        <Button
                            className={classes.verifyButton}
                            loading={processingVerify}
                            disabled={!aadharCardVerified || !otpFetched || processingVerify || processingSubmit || !isDrivingLicenceNumberLengthValid || digilockerValidationFailed}
                            onClick={verifyDrivingLicenseAndCaptcha}>
                            {intl.formatMessage({ id: "verifyDrivingLicence" })}
                        </Button>
                    </div>
                </Form.Item>) : null}

                <div className={classes.formSectionHeader}>
                    {intl.formatMessage({ id: "documents" })} :
                </div>
                <Form.Item
                    label={`${intl.formatMessage({ id: 'aadhaarCardPhoto' })} ::`}
                    name={`aadhaarCardPhoto`}
                    rules={[
                        {
                            required: true,
                            message: `${intl.formatMessage({ id: "aadhaarCardPhoto" })} is required!`,
                        },
                    ]}
                >
                    <Upload
                        accept=".png, .jpg, .jpeg"
                        multiple={true}
                        maxCount={2}
                        showUploadList={true}
                        listType="picture-card"
                        className={classes.uploadField}
                        fileList={fileList['aadhaarCard']}
                        {...uploadProps('aadhaarCard')}
                    >
                        <UploadOutlined className={classes.uploadIcon} />
                        <div>{intl.formatMessage({ id: "chooseFile" })}</div>
                        <p>- {intl.formatMessage({ id: "maxiumumPhotosAllowed" })}</p>
                        <p>- {intl.formatMessage({ id: "maxiumumSizeAllowed" })}</p>
                        <p>- {intl.formatMessage({ id: "allowedFormats" })}</p>
                    </Upload>
                </Form.Item>
                <Form.Item
                    label={`${intl.formatMessage({ id: 'drivingLicencePhoto' })} ::`}
                    name={'drivingLicencePhoto'}
                    dependencies={["workerType"]}
                    rules={[
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (getFieldValue('workerType') === workerTypeEnum.DRIVER && !value) {
                                    return Promise.reject('Please upload Driving License Photo!');
                                }
                                return Promise.resolve();
                            }
                        }),
                        ({ getFieldValue }) => ({
                            required: getFieldValue('workerType') === workerTypeEnum.DRIVER,
                            message: `${intl.formatMessage({ id: "drivingLicencePhoto" })} is required!`
                        }),
                    ]}
                >
                    <Upload
                        accept=".png, .jpg, .jpeg"
                        multiple={true}
                        maxCount={2}
                        showUploadList={true}
                        listType="picture-card"
                        className={classes.uploadField}
                        fileList={fileList['drivingLicence']}
                        {...uploadProps('drivingLicence')}
                    >
                        <UploadOutlined className={classes.uploadIcon} />
                        <div>{intl.formatMessage({ id: "chooseFile" })}</div>
                        <p>- {intl.formatMessage({ id: "maxiumumPhotosAllowed" })}</p>
                        <p>- {intl.formatMessage({ id: "maxiumumSizeAllowed" })}</p>
                        <p>- {intl.formatMessage({ id: "allowedFormats" })}</p>
                    </Upload>
                </Form.Item>

                {aadharCardVerified && otpFetched &&
                    (!isWorkerTypeDriver || drivingLicenseVerified) && (<Form.Item label=":">
                        <div>
                            <ReCAPTCHA
                                sitekey={GOOGLE_RECAPTCHA_SITE_KEY}
                                onChange={captchaChange}
                                hl={language}
                                key={`${language}${formReset}`}
                            />
                            <span style={{ color: "red" }}>{captchaError}</span>
                        </div>
                    </Form.Item>)}
                <Form.Item label=":">
                    <Button
                        style={{ width: "100%" }}
                        size="large"
                        disabled={processingVerify || digilockerValidationFailed || !aadharCardVerified || !otpFetched ||
                            (form.getFieldValue('workerType') === workerTypeEnum.DRIVER && !drivingLicenseVerified)}
                        type="primary"
                        htmlType="submit"
                        loading={processingSubmit}
                    >
                        {intl.formatMessage({ id: "submit" })}
                    </Button>
                </Form.Item>
            </div>
            <Modal
                open={previewOpen["drivingLicence"]}
                title={previewTitle}
                footer={null}
                onCancel={handleCancel}
            >
                <img
                    alt="example"
                    style={{ width: "100%" }}
                    src={previewImage["drivingLicence"]}
                />
            </Modal>
            <Modal
                open={previewOpen["aadhaarCard"]}
                title={previewTitle}
                footer={null}
                onCancel={handleCancel}
            >
                <img
                    alt="example"
                    style={{ width: "100%" }}
                    src={previewImage["aadhaarCard"]}
                />
            </Modal>
        </Form>
    </>
    );
};

const mapStateToProps = (state) => {
    return {
        masterdata: state.masterdata,
    };
};

export default connect(mapStateToProps)(QwiklogisticsRiderOnboardingForm);
